import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback } from 'react'

import { Balance, InfoOutlined, Payments } from '@mui/icons-material'
import { Tag } from '@workwave-tidal/core/components/Tag'

import {
  useController,
  type DriverUsage,
  type OptimizationStrategy as Strategy,
} from '../../hooks/useController'
import { useTexts } from '../../useTexts'
import { BalanceBy } from './BalanceBy'
import { ConstraintAlert } from './ConstraintAlert'
import { DriverUsageToggle } from './DriverUsageToggle'

export function OptimizationStrategy() {
  const texts = useTexts()
  const {
    data: { strategy, driverUsage, constraintScenario, ignoreConstraints },
    updateData,
  } = useController()

  // Balanced performance can't be selected while a minimum-constraint conflict is
  // active until the user acknowledges it via the "Ignore" checkbox. The Start
  // button itself stays enabled (Fleet savings is always runnable) — this gates
  // the option, not the optimization.
  const balancedDisabled = constraintScenario !== 'none' && !ignoreConstraints

  const handleStrategyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateData({ strategy: event.target.value as Strategy })
    },
    [updateData],
  )

  const handleDriverUsageChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, value: DriverUsage | null) => {
      if (value === null) return
      updateData({ driverUsage: value })
    },
    [updateData],
  )

  return (
    <Stack spacing={2.5}>
      <FormControl component="fieldset">
        <FormLabel
          component="legend"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 1,
            color: 'text.secondary',
            fontSize: 16,
            '&.Mui-focused': {
              color: 'text.secondary',
            },
          }}
        >
          {texts.optimizationStrategyLabel}
          <Tooltip title={texts.optimizationStrategyTooltip} placement="top" arrow>
            <InfoOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
          </Tooltip>
        </FormLabel>

        <RadioGroup value={strategy} onChange={handleStrategyChange}>
          {/* Fleet savings */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormControlLabel
                value="fleetSavings"
                control={
                  <Radio
                    data-trackid="route-optimization__strategy-fleet"
                    data-testid="route-optimization__strategy-fleet"
                  />
                }
                label={<Typography variant="body1">{texts.fleetSavings}</Typography>}
              />
              <Tag
                variant="filledOutline"
                color="success"
                icon={<Payments sx={{ fontSize: 14 }} />}
                label={texts.fleetSavingsBadge}
              />
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: 4, mt: -0.5, mb: strategy === 'fleetSavings' ? 1.5 : 0 }}
            >
              {texts.fleetSavingsDescription}
            </Typography>

            {strategy === 'fleetSavings' && (
              <Box sx={{ ml: 4 }}>
                <DriverUsageToggle
                  value={driverUsage}
                  onChange={handleDriverUsageChange}
                  minimizeDriversIsDefault
                />
              </Box>
            )}
          </Box>

          {/* Balanced performance */}
          <Box mt={2}>
            <Tooltip
              title={balancedDisabled ? texts.balancedPerformanceDisabledTooltip : ''}
              placement="top"
              arrow
              disableHoverListener={!balancedDisabled}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ width: 'fit-content' }}>
                <FormControlLabel
                  value="balancedPerformance"
                  control={
                    <Radio
                      disabled={balancedDisabled}
                      data-trackid="route-optimization__strategy-balanced"
                      data-testid="route-optimization__strategy-balanced"
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      color={balancedDisabled ? 'text.disabled' : 'text.primary'}
                    >
                      {texts.balancedPerformance}
                    </Typography>
                  }
                />
                <Tag
                  variant="filledOutline"
                  color="secondary"
                  disabled={balancedDisabled}
                  icon={<Balance sx={{ fontSize: 14 }} />}
                  label={texts.balancedPerformanceBadge}
                />
              </Stack>
            </Tooltip>
            <Typography
              variant="body2"
              color={balancedDisabled ? 'text.disabled' : 'text.secondary'}
              sx={{ ml: 4, mt: -0.5 }}
            >
              {texts.balancedPerformanceDescription}
            </Typography>

            {strategy === 'balancedPerformance' && (
              <Stack spacing={2.5} sx={{ ml: 4, mt: 1.5 }}>
                <DriverUsageToggle
                  value={driverUsage}
                  onChange={handleDriverUsageChange}
                  disabled={balancedDisabled}
                />
                <BalanceBy disabled={balancedDisabled} />
              </Stack>
            )}

            {constraintScenario !== 'none' && (
              <Box sx={{ ml: 4, mt: 1.5 }}>
                <ConstraintAlert />
              </Box>
            )}
          </Box>
        </RadioGroup>
      </FormControl>
    </Stack>
  )
}
